import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCompanyWorkForce } from '../../context/CompanyWorkForceContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import { useAuth } from '../../context/AuthContext';
import {
  UserIcon,
  BriefcaseIcon,
  MapPinIcon,
  PhoneIcon,
  CalendarIcon,
  IdentificationIcon,
  ShieldCheckIcon,
  ArrowLeftIcon,
  PencilIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

const CompanyWorkForceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workForce, getWorkForce, getWorkForceById } = useCompanyWorkForce();
  const {  hideLoading } = useLoading();
  const getRoutePath = useRoutePath();

  const [worker, setWorker] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const hasFetched = useRef(false);

  const fetchWorker = useCallback(
    async () => {
      if (!id || hasFetched.current) return;
      hasFetched.current = true;

      setIsFetching(true);
      //showLoading();

      try {
        // Try to get from already loaded context first
        let found = getWorkForceById(id);

        // If not found (e.g. direct URL hit), load all workforce once
        if (!found) {
          const response = await getWorkForce(1, { all: true });

          if (response) {
            const list = Array.isArray(response?.data)
              ? response.data
              : response.data?.data;

            if (Array.isArray(list)) {
              found = list.find((item) => Number(item.id) === Number(id));
            }
          }
        }

        if (found) {
          setWorker(found);
        } else {
          setWorker(null);
        }
      } catch (error) {
        console.error('Error fetching workforce member:', error);
        setWorker(null);
      } finally {
        setIsFetching(false);
        hideLoading();
      }
    },
    [id, getWorkForceById, getWorkForce, hideLoading]
  );

  useEffect(() => {
    // If list is already in context we might be able to resolve quickly
    if (id && workForce?.data?.length && !worker) {
      const existing = workForce.data.find(
        (item) => Number(item.id) === Number(id)
      );
      if (existing) {
        setWorker(existing);
        setIsFetching(false);
        return;
      }
    }

    fetchWorker();
  }, [id, workForce, worker, fetchWorker]);

  const getStatusBadge = () => {
    const isActive = worker?.active;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getFieldWorkerBadge = () => {
    if (worker?.is_field_worker == null) return null;

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
          worker.is_field_worker
            ? 'bg-sky-100 text-sky-800'
            : 'bg-gray-100 text-gray-700'
        }`}
      >
        {worker.is_field_worker ? 'Field Worker' : 'Office Staff'}
      </span>
    );
  };

  const getAssignedCompaniesText = () => {
    if (Array.isArray(worker?.companies) && worker.companies.length) {
      return worker.companies.map((c) => c.name).join(', ');
    }

    if (Array.isArray(worker?.company_ids) && worker.company_ids.length) {
      return `${worker.company_ids.length} assigned`;
    }

    return 'N/A';
  };

  const canEdit =
    !!user &&
    (user.role_id === 1 ||
      user.role_id === 2 ||
      Number(user.id) === Number(worker?.id));

  if (!isFetching && !worker) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Workforce Member Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The workforce member you&apos;re looking for doesn&apos;t exist or has
            been removed.
          </p>
          <button
            onClick={() => navigate(getRoutePath('/company-workforces'))}
            className="inline-flex items-center px-4 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Workforce
          </button>
        </div>
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-8 w-8 border-2 border-[#009efb] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">
            Loading workforce member details...
          </p>
        </div>
      </div>
    );
  }

  const fullName = `${worker.first_name || ''} ${
    worker.last_name || ''
  }`.trim();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            {/* Left side */}
            <div className="flex items-start md:items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <UserIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center flex-wrap gap-2">
                  <span>{fullName || 'Workforce Member Details'}</span>
                  {worker.job_title && (
                    <span className="text-sm font-medium text-gray-500">
                      ({worker.job_title})
                    </span>
                  )}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  {worker.employee_id && (
                    <span>Employee ID #{worker.employee_id}</span>
                  )}
                  <span>{getStatusBadge()}</span>
                  {getFieldWorkerBadge()}
                </div>
              </div>
            </div>

            {/* Right side buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate(getRoutePath('/company-workforces'))}
                className="inline-flex items-center justify-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>

              {canEdit && (
                <button
                  onClick={() =>
                    navigate(getRoutePath(`/company-workforces/edit/${worker.id}`))
                  }
                  className="inline-flex items-center justify-center px-4 py-2 text-sm bg-[#009efb] text-white rounded-md hover:bg-[#0089db] transition-colors w-full sm:w-auto"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit Member
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="xl:col-span-2 space-y-6">
            {/* Profile Overview Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Profile Overview
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="flex-shrink-0">
                    {worker.image?.path ? (
                      <img
                        src={worker.image.path}
                        alt={fullName || 'Profile'}
                        className="h-20 w-20 rounded-full object-cover bg-gray-50 border border-gray-200"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center">
                        {fullName ? (
                          <span className="text-lg font-semibold text-gray-600">
                            {worker.first_name?.[0]}
                            {worker.last_name?.[0]}
                          </span>
                        ) : (
                          <PhotoIcon className="h-8 w-8 text-gray-400" />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Name
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {fullName || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Department
                        </p>
                        <p className="text-sm text-gray-900">
                          {worker.department || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Employee Type
                        </p>
                        <p className="text-sm text-gray-900">
                          {worker.employee_type?.name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Role
                        </p>
                        <p className="text-sm text-gray-900">
                          {worker.role?.name || 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Username
                        </p>
                        <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                          {worker.username || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Display Name on Work Orders
                        </p>
                        <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                          {worker.display_name_on_work_orders || fullName || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact & Address Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <PhoneIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Contact & Address
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* Contact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900 break-all">
                      {worker.email || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Phone
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {worker.primary_number || 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <MapPinIcon className="h-5 w-5 text-gray-500 mr-2" />
                    Address
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <span className="text-gray-600 block mb-1">Street Address 1</span>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                        {worker.address1 || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600 block mb-1">Street Address 2</span>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                        {worker.address2 || 'N/A'}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-gray-600 block mb-1">City</span>
                        <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                          {worker.city || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600 block mb-1">State / Province</span>
                        <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                          {worker.state || 'N/A'}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-gray-600 block mb-1">
                          Zip / Postal Code
                        </span>
                        <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                          {worker.zipcode || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600 block mb-1">
                          Emergency Contact
                        </span>
                        <div className="p-3 bg-gray-50 rounded-lg text-gray-900 space-y-1">
                          <div>
                            <span className="font-medium">
                              {worker.emergency_contact_name || 'N/A'}
                            </span>
                          </div>
                          {worker.emergency_contact_number && (
                            <div className="text-sm text-gray-700">
                              {worker.emergency_contact_number}
                            </div>
                          )}
                          {worker.emergency_contact_relation && (
                            <div className="text-xs text-gray-500">
                              Relation: {worker.emergency_contact_relation}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Employment Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <BriefcaseIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Employment Details
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hire Date
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {worker.hire_date || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Original Hire Date
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {worker.original_hire_date || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adjusted Service Date
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {worker.adjusted_service_date || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Release Date
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {worker.release_date || 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Manager / Supervisor
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {worker.manager?.first_name || worker.manager?.last_name
                        ? `${worker.manager?.first_name || ''} ${
                            worker.manager?.last_name || ''
                          }`.trim()
                        : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned Companies
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {getAssignedCompaniesText()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Summary & Payroll */}
          <div className="space-y-6">
            {/* Account Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center">
                  <IdentificationIcon className="h-5 w-5 text-gray-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Account Summary
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  {getStatusBadge()}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Account Locked</span>
                  <span className="font-medium text-gray-900">
                    {worker.account_locked_status ? 'Yes' : 'No'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">One-time User</span>
                  <span className="font-medium text-gray-900">
                    {worker.is_one_time_user ? 'Yes' : 'No'}
                  </span>
                </div>

                {worker.formated_created_at && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-1 flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Created At
                    </div>
                    <div className="text-sm text-gray-900">
                      {worker.formated_created_at}
                    </div>
                  </div>
                )}

                {worker.formated_updated_at && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1 flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Last Updated
                    </div>
                    <div className="text-sm text-gray-900">
                      {worker.formated_updated_at}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Payroll Snapshot Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center">
                  <ShieldCheckIcon className="h-5 w-5 text-gray-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Payroll Snapshot
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Regular Rate</span>
                  <span className="font-medium text-gray-900">
                    {worker.regular_rate || '-'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Commission Enabled</span>
                  <span className="font-medium text-gray-900">
                    {worker.pay_sales_commissions ? 'Yes' : 'No'}
                  </span>
                </div>

                {worker.pay_sales_commissions && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Commission Type</span>
                      <span className="font-medium text-gray-900">
                        {worker.commission_rate_type || '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Commission Rate</span>
                      <span className="font-medium text-gray-900">
                        {worker.commission_rate || '-'}
                      </span>
                    </div>
                  </>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    For full employment, schedule, and payroll details, use the{' '}
                    <Link
                      to={getRoutePath(`/company-workforces/edit/${worker.id}`)}
                      className="text-[#009efb] hover:text-[#0089db] font-medium"
                    >
                      Edit Workforce Member
                    </Link>{' '}
                    screen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyWorkForceView;